import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, skip, switchMap } from 'rxjs/operators';
import { CustomSelectComponent, CustomSelectOption } from '../../shared/custom-select/custom-select.component';
import { AuthService } from '../../../services/auth.service';
import { CreateProviderReportPayload, UpdateProviderReportPayload, ReportsApiService } from '../../../services/reports-api.service';
import { EntityFinancialReportRow, mapProviderReport } from '../reports.types';

interface ProviderSummaryRow {
  kind: 'group' | 'custom' | 'total';
  label: string;
  count?: number;
  amount: number;
  source?: EntityFinancialReportRow;
}

@Component({
  selector: 'app-reports-provider',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, CustomSelectComponent],
  templateUrl: './reports-provider.component.html',
  styleUrls: ['./reports-provider.component.scss']
})
export class ReportsProviderComponent implements OnInit, OnDestroy {
  private subscriptions = new Subscription();
  private currentLoadSub: Subscription | null = null;

  reportRows: EntityFinancialReportRow[] = [];
  selectedMonth = String(new Date().getMonth() + 1).padStart(2, '0');
  selectedYear = String(new Date().getFullYear());
  isLoading = false;
  loadingError: string | null = null;
  readonly locale = 'it-IT';

  providerCustomRow: CreateProviderReportPayload = this.createEmptyCustomRow();
  editingEntryId: string | null = null;
  isCreatingCustomRow = false;
  isSavingCustomRow = false;
  customRowError: string | null = null;

  deleteTarget: EntityFinancialReportRow | null = null;
  isDeletingCustomRow = false;
  deleteError: string | null = null;

  constructor(
    private authService: AuthService,
    private reportsApi: ReportsApiService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.authService.selectedDcr$.pipe(
        distinctUntilChanged(),
        skip(1),
        switchMap(() => this.authService.waitForEntitySync())
      ).subscribe(() => {
        this.cancelCustomRow();
        this.loadReports();
      })
    );
    this.subscriptions.add(
      this.authService.waitForEntitySync().subscribe(() => this.loadReports())
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  onYearChange(): void {
    if (this.selectedYear === 'all') {
      this.selectedMonth = 'all';
    } else {
      const now = new Date();
      this.selectedMonth = Number(this.selectedYear) === now.getFullYear()
        ? String(now.getMonth() + 1).padStart(2, '0')
        : '12';
    }
    this.loadReports();
  }

  loadReports(): void {
    const entityID = this.authService.getActiveDcrId();
    if (!entityID) {
      this.reportRows = [];
      return;
    }
    this.currentLoadSub?.unsubscribe();
    this.isLoading = true;
    this.loadingError = null;
    const { startDate, endDate } = this.getSelectedDateRange();
    this.currentLoadSub = this.reportsApi.getProviderReports({ startDate, endDate }).subscribe({
      next: (rows) => {
        const entityName = this.authService.getDcrNameById(entityID) || entityID;
        this.reportRows = (rows || [])
          .map(row => mapProviderReport(row, entityName))
          .sort((a, b) => b.reportDate.localeCompare(a.reportDate));
        this.isLoading = false;
      },
      error: (error) => {
        this.loadingError = (error as Error)?.message || this.translate.instant('reports.errors.load');
        this.reportRows = [];
        this.isLoading = false;
      }
    });
    this.subscriptions.add(this.currentLoadSub);
  }

  /**
   * Global data scope = global access with no entity selected. Provider reports are per-entity,
   * so the page asks for an entity instead of showing an arbitrary one's numbers.
   */
  get hasGlobalDataScope(): boolean {
    return this.authService.hasGlobalDataScope();
  }

  get hasGlobalDataAccess(): boolean {
    return this.authService.hasGlobalDataAccess();
  }

  get showCustomRowTrigger(): boolean {
    return this.hasGlobalDataAccess && !this.isLoading && !this.loadingError;
  }

  get providerSummaryRows(): ProviderSummaryRow[] {
    const rows: ProviderSummaryRow[] = [];

    const audienceRows = this.reportRows.filter(r => r.entryType === 'audience');
    if (audienceRows.length) {
      const totalQty = audienceRows.reduce((s, r) => s + r.quantity, 0);
      const amount = audienceRows.reduce((s, r) => s + r.amount, 0);
      rows.push({
        kind: 'group',
        label: this.translate.instant('reports.provider.summary.audienceTotal'),
        count: totalQty,
        amount
      });
    }

    this.reportRows
      .filter(r => r.entryType === 'custom')
      .forEach(r => rows.push({
        kind: 'custom',
        label: r.description || this.translate.instant('reports.entryTypes.custom'),
        count: 1,
        amount: r.amount,
        source: r
      }));

    if (rows.length) {
      rows.push({
        kind: 'total',
        label: this.translate.instant('reports.provider.summary.total'),
        amount: this.reportRows.reduce((s, r) => s + r.amount, 0)
      });
    }

    return rows;
  }

  get monthOptions(): CustomSelectOption[] {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    const selectedYearNum = this.selectedYear === 'all' ? null : Number(this.selectedYear);
    const maxMonth = selectedYearNum === currentYear ? currentMonth : 12;

    const lang = this.translate.currentLang || 'it';
    const formatter = new Intl.DateTimeFormat(lang, { month: 'long' });
    const months = Array.from({ length: maxMonth }, (_, i) => {
      const monthNum = i + 1;
      const label = formatter.format(new Date(2026, monthNum - 1, 1));
      return {
        value: String(monthNum).padStart(2, '0'),
        label: label.charAt(0).toUpperCase() + label.slice(1)
      };
    });
    return [
      { value: 'all', label: this.translate.instant('reports.filters.monthOptions.all') },
      ...months
    ];
  }

  get yearOptions(): CustomSelectOption[] {
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 6 }, (_, i) => currentYear - 5 + i);
    return [
      { value: 'all', label: this.translate.instant('reports.filters.yearOptions.all') },
      ...years.map(y => ({ value: String(y), label: String(y) }))
    ];
  }

  getEntryTypeLabel(value: string): string {
    return this.translate.instant(`reports.entryTypes.${value}`, { defaultValue: value });
  }

  startCustomRow(): void {
    if (!this.hasGlobalDataAccess || this.isCreatingCustomRow) return;
    const entityID = this.authService.getActiveDcrId() || '';
    this.editingEntryId = null;
    this.providerCustomRow = {
      ...this.createEmptyCustomRow(),
      DataProviderID: entityID,
      ReportDate: new Date().toISOString().slice(0, 10)
    };
    this.customRowError = null;
    this.isCreatingCustomRow = true;
  }

  startEditCustomRow(source: EntityFinancialReportRow): void {
    if (!this.hasGlobalDataAccess || this.isCreatingCustomRow) return;
    this.editingEntryId = source.entryId;
    this.providerCustomRow = {
      DataProviderID: source.entityId,
      EntryID: source.entryId ?? undefined,
      ReportDate: source.reportDate,
      EntryDescription: source.description ?? '',
      Earnings: Math.round(source.amount * 100) / 100
    };
    this.customRowError = null;
    this.isCreatingCustomRow = true;
  }

  cancelCustomRow(): void {
    this.providerCustomRow = this.createEmptyCustomRow();
    this.editingEntryId = null;
    this.customRowError = null;
    this.isCreatingCustomRow = false;
    this.isSavingCustomRow = false;
  }

  saveCustomRow(): void {
    const entityID = this.authService.getActiveDcrId();
    if (!this.hasGlobalDataAccess || !entityID || !this.providerCustomRow.ReportDate) {
      this.customRowError = this.translate.instant('reports.errors.customRowInvalid');
      return;
    }
    this.isSavingCustomRow = true;
    this.customRowError = null;

    if (this.editingEntryId) {
      const payload: UpdateProviderReportPayload = {
        EntryID: this.editingEntryId,
        ReportDate: this.providerCustomRow.ReportDate,
        EntryDescription: this.providerCustomRow.EntryDescription?.trim() || '',
        Earnings: Math.round((Number(this.providerCustomRow.Earnings) || 0) * 100) / 100
      };
      const sub = this.reportsApi.updateProviderReport(payload).subscribe({
        next: () => {
          this.cancelCustomRow();
          this.loadReports();
        },
        error: (error) => {
          this.customRowError = (error as Error)?.message || this.translate.instant('reports.errors.customRowSave');
          this.isSavingCustomRow = false;
        }
      });
      this.subscriptions.add(sub);
    } else {
      const payload: CreateProviderReportPayload = {
        DataProviderID: entityID,
        EntryID: crypto.randomUUID(),
        ReportDate: this.providerCustomRow.ReportDate,
        EntryDescription: this.providerCustomRow.EntryDescription?.trim() || '',
        Earnings: Math.round((Number(this.providerCustomRow.Earnings) || 0) * 100) / 100
      };
      const sub = this.reportsApi.createProviderReport(payload).subscribe({
        next: () => {
          this.cancelCustomRow();
          this.loadReports();
        },
        error: (error) => {
          this.customRowError = (error as Error)?.message || this.translate.instant('reports.errors.customRowSave');
          this.isSavingCustomRow = false;
        }
      });
      this.subscriptions.add(sub);
    }
  }

  private createEmptyCustomRow(): CreateProviderReportPayload {
    return { DataProviderID: '', ReportDate: '', EntryDescription: '', Earnings: 0 };
  }

  confirmDeleteCustomRow(row: EntityFinancialReportRow): void {
    if (!this.hasGlobalDataAccess || !row.entryId) return;
    this.deleteTarget = row;
    this.deleteError = null;
  }

  cancelDeleteCustomRow(): void {
    if (this.isDeletingCustomRow) return;
    this.deleteTarget = null;
    this.deleteError = null;
  }

  executeDeleteCustomRow(): void {
    if (!this.deleteTarget?.entryId || this.isDeletingCustomRow) return;
    this.isDeletingCustomRow = true;
    this.deleteError = null;
    const sub = this.reportsApi.deleteProviderReport(this.deleteTarget.entryId).subscribe({
      next: () => {
        this.deleteTarget = null;
        this.isDeletingCustomRow = false;
        this.loadReports();
      },
      error: (error) => {
        this.deleteError = (error as Error)?.message || this.translate.instant('reports.errors.customRowSave');
        this.isDeletingCustomRow = false;
      }
    });
    this.subscriptions.add(sub);
  }

  private getSelectedDateRange(): { startDate: string; endDate: string } {
    const fallbackYear = new Date().getFullYear();

    if (this.selectedYear === 'all' && this.selectedMonth === 'all') {
      return { startDate: '2000-01-01', endDate: '2999-12-31' };
    }

    const year = this.selectedYear === 'all' ? fallbackYear : Number(this.selectedYear);

    if (this.selectedMonth === 'all') {
      return { startDate: `${year}-01-01`, endDate: `${year}-12-31` };
    }

    const month = Number(this.selectedMonth);
    const lastDay = new Date(year, month, 0).getDate();
    return {
      startDate: `${year}-${this.selectedMonth}-01`,
      endDate: `${year}-${this.selectedMonth}-${String(lastDay).padStart(2, '0')}`
    };
  }
}
